/**
 * PrivacyPolicy - the privacy notice
 * -----------------------------------
 * The page furniture - heading, amber banner, last updated - and the choice of
 * which language's document to show. The notices themselves are
 * `legal/PrivacyEn.jsx` and `legal/PrivacyIt.jsx`, and THOSE are the canonical
 * versions: they are what people actually see, so they are what counts.
 *
 * Same structure and same reasoning as Terms.jsx - a legal document is read
 * and translated as a document, not as four hundred dictionary keys, and the
 * only property that matters is that the two say the same thing.
 * `npm run check-legal` compares their shape.
 *
 * Neither is marked authoritative. Question 13 in LEGAL_REVIEW.md.
 *
 * TWO THINGS THAT USED TO BE WARNINGS HERE, and are not any more:
 *
 *   1. The controller's postal address was a placeholder. It was filled in on
 *      28 August 2026 - section 1, in both languages - and this comment went
 *      on saying otherwise for a month, which is how a stale warning wastes
 *      somebody's afternoon.
 *   2. An amber "awaiting legal review" banner sat above the document, saying
 *      not to open the site to real users until a professional had read it.
 *      The owner decided on 24 September 2026 not to commission that review,
 *      so the banner was removed rather than left promising something that is
 *      not coming. The decision, and what it costs, are written down in
 *      LEGAL_REVIEW.md section 7.
 */

import { ShieldCheck } from 'lucide-react';

import BackLink from '../components/BackLink';
import { useI18n } from '../hooks/useI18n';
import PrivacyEn from './legal/PrivacyEn';
import PrivacyIt from './legal/PrivacyIt';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'ChoreConnect';
/**
 * The date is written out per language rather than once in English.
 *
 * It used to be a single string, so the Italian page read "Ultimo
 * aggiornamento: 17 August 2026" - an English month in an Italian sentence,
 * on the one page where being read carefully is the whole point. Small, and
 * exactly the kind of thing that makes a document look translated rather
 * than written.
 */
const LAST_UPDATED = { en: '25 September 2026', it: '25 settembre 2026' };

export default function PrivacyPolicy() {
  const { lang } = useI18n();
  const italian = lang === 'it';

  return (
    <div className="container section" style={{ maxWidth: 780 }}>
      <BackLink to="/" label={italian ? 'Torna a ChoreConnect' : 'Back to ChoreConnect'} />

      <div className="page-header">
        <span className="feature-icon">
          <ShieldCheck size={26} aria-hidden="true" />
        </span>
        <h1>{italian ? 'Informativa sulla privacy' : 'Privacy notice'}</h1>
        <p>
          {italian
            ? `Come ${APP_NAME} tratta i tuoi dati. Ultimo aggiornamento: ${LAST_UPDATED.it}.`
            : `How ${APP_NAME} looks after your information. Last updated ${LAST_UPDATED.en}.`}
        </p>
      </div>

      {italian ? <PrivacyIt /> : <PrivacyEn />}
    </div>
  );
}
